/**
 * Proxy base URL. Empty string in dev: requests go same-origin and the Vite
 * dev server proxies /api and /geoserver to the mock (or a real proxy).
 * In production builds, set VITE_PROXY_BASE to the proxy origin
 * (e.g. https://geo.digigeodata.com) and all requests become credentialed
 * cross-origin calls (PRD P0.2).
 */
export const PROXY_BASE: string = (import.meta.env.VITE_PROXY_BASE ?? '').replace(/\/+$/, '')

/** True when the proxy is on another origin — tile/image loads must send credentials. */
export const CROSS_ORIGIN: boolean = PROXY_BASE !== ''

/**
 * Link target for the seat-limit banner (Active Sessions card, PRD P0.7).
 * Defaults to the proxy's /dashboard (the dashboard lives on the API origin),
 * so in a prod build it follows PROXY_BASE (e.g. https://geo.digigeodata.com).
 * Override with VITE_DASHBOARD_URL only if the dashboard moves elsewhere.
 * No staging default.
 */
export const DASHBOARD_URL: string =
  import.meta.env.VITE_DASHBOARD_URL ?? (PROXY_BASE ? `${PROXY_BASE}/dashboard` : '/dashboard')

/**
 * Link target for the login screen's "Forgot your password?" link.
 * The reset flow is a server-rendered proxy page (no JSON/CORS surface), so
 * this is a same-tab, full navigation away from the viewer — not a fetch.
 * Defaults to the proxy's /forgot-password, following PROXY_BASE like
 * DASHBOARD_URL. Override with VITE_FORGOT_PASSWORD_URL only if that page moves.
 */
export const FORGOT_PASSWORD_URL: string =
  import.meta.env.VITE_FORGOT_PASSWORD_URL ??
  (PROXY_BASE ? `${PROXY_BASE}/forgot-password` : '/forgot-password')

/**
 * Google Maps Platform — Map Tiles API
 *
 * Session token endpoint : POST https://tile.googleapis.com/v1/createSession?key=<KEY>
 * Body                   : { mapType: "roadmap"|"satellite"|"terrain", language: "fr", region: "FR" }
 * Tile URL               : https://tile.googleapis.com/v1/2dtiles/{z}/{x}/{y}?session=<TOKEN>&key=<KEY>
 * Attribution            : logo Google (min 16dp) + copyright retourné par l'API, coin bas-droit obligatoire
 * Token validity         : ~2 semaines — cache en mémoire uniquement (jamais localStorage)
 * Ref doc                : https://developers.google.com/maps/documentation/tile/session_tokens
 *
 * La clé doit être restreinte par référent HTTP dans Google Cloud Console.
 * Les fonds Google sont désactivés si VITE_GOOGLE_MAPS_API_KEY est vide.
 */
export const GOOGLE_MAPS_API_KEY: string = import.meta.env.VITE_GOOGLE_MAPS_API_KEY ?? ''

/** Interrupteur explicite. Si absent ou "false", les fonds Google n'apparaissent pas même avec une clé. */
export const GOOGLE_BASEMAPS_ENABLED: boolean =
  GOOGLE_MAPS_API_KEY !== '' && import.meta.env.VITE_GOOGLE_BASEMAPS_ENABLED !== 'false'

/** Company lifecycle statuses the proxy rejects with 403 (PRD P0.1). */
export const INACTIVE_LIFECYCLE_STATUSES = new Set([
  'waiting_contract',
  'expired',
  'canceled',
  'suspended',
])
